import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import { styled } from '@activebrands/core-web/libs/styletron';
import Paragraph from 'components/text/Paragraph';

const Wrapper = styled('div', {
    display: 'flex',
    padding: '12px var(--margin-content)',
    borderBottom: '1px solid var(--color-border-checkout-subtle)',
});

const AddressSection = styled('div', {
    width: '100%',
    marginBottom: '20px',

    ':last-of-type': {
        marginBottom: '0',
    },
});

const AddressRow = styled('p', {
    marginBottom: '2px',
    ...textStyles['Founders Grotesk/16-140'],
});

const BillingAddressWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
});

const ShippingAddressWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
});

const ShippingAddressSummary = ({
    address = {},
    shippingAddress = {},
    separateBillingAndShippingAddress = false,
    ...rest
}) => {
    const countries = useSelector(state => state.ecommerce.countries);
    const country = countries[address.country];
    const states = country.states
        ? Object.keys(country.states).map(key => ({ label: country.states[key], value: key }))
        : [];

    const choosenBillingState = address?.state && states?.filter(s => s.value === address.state)?.[0];
    const choosenShippingState = shippingAddress?.state && states?.filter(s => s.value === shippingAddress.state)?.[0];

    return (
        <Wrapper {...rest}>
            <BillingAddressWrapper
                $style={{
                    flexBasis: separateBillingAndShippingAddress ? '50%' : 'unset',
                }}
            >
                {separateBillingAndShippingAddress && (
                    <Paragraph
                        $style={{
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                        }}
                        textStyles="Founders Grotesk Condensed/16-100"
                    >
                        {fm('Billing Information')}
                    </Paragraph>
                )}

                <AddressSection>
                    <AddressRow>
                        {address?.firstName} {address.lastName}
                    </AddressRow>
                    <AddressRow>{address?.address1}</AddressRow>
                    {address.address2 && <AddressRow>{address.address2}</AddressRow>}
                    <AddressRow>
                        {address?.zip} {address?.city}
                    </AddressRow>
                    <AddressRow>
                        {choosenBillingState && `${choosenBillingState.label}, `}
                        {country?.name}
                    </AddressRow>
                </AddressSection>
                <AddressSection>
                    <AddressRow>{address?.email}</AddressRow>
                    <AddressRow>{address?.phone}</AddressRow>
                </AddressSection>
            </BillingAddressWrapper>
            {separateBillingAndShippingAddress && (
                <ShippingAddressWrapper>
                    <Paragraph
                        $style={{
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                        }}
                        textStyles="Founders Grotesk Condensed/16-100"
                    >
                        {fm('Shipping address')}
                    </Paragraph>
                    <AddressSection>
                        <AddressRow>
                            {shippingAddress?.firstName} {shippingAddress.lastName}
                        </AddressRow>
                        <AddressRow>{shippingAddress?.address1}</AddressRow>
                        {shippingAddress.address2 && <AddressRow>{shippingAddress.address2}</AddressRow>}
                        <AddressRow>
                            {shippingAddress?.zip} {shippingAddress?.city}
                        </AddressRow>
                        <AddressRow>
                            {choosenShippingState && `${choosenShippingState.label}, `}
                            {country?.name}
                        </AddressRow>
                    </AddressSection>
                    <AddressSection>
                        <AddressRow>{shippingAddress?.email}</AddressRow>
                        <AddressRow>{shippingAddress?.phone}</AddressRow>
                    </AddressSection>
                </ShippingAddressWrapper>
            )}
        </Wrapper>
    );
};

ShippingAddressSummary.propTypes = {
    address: PropTypes.object,
    separateBillingAndShippingAddress: PropTypes.bool,
    shippingAddress: PropTypes.object,
};

export default ShippingAddressSummary;
